import { useSelector } from "react-redux";
import { Plan, Service, Subscription } from "../store/models/index";

export { PlanIds } from "../store/models/index";

export const isWorklabSubscription = (subscription?: Subscription | null) =>
	String(
		subscription?.services?.find((s: Service) => s.service === "worklab")?.value
	) === "true";

export const hasOperationalDashboardAccess = (
	subscription?: Subscription | null
) =>
	Array.isArray(subscription?.services) && !isWorklabSubscription(subscription);

export const isSubscriptionServiceBlocked = (service?: Service) =>
	String(service?.value) === "false";

export const isSubscriptionLimitReached = (
	limitValue: Service["value"] | undefined | null,
	currentValue: number | undefined | null
) =>
	limitValue !== "unlimited" &&
	limitValue !== undefined &&
	limitValue !== null &&
	(currentValue as number) >= Number(limitValue);

export const hasSubscriptionServiceOption = (
	service: Service | undefined,
	option: string
) => {
	const value = service?.value;

	if (Array.isArray(value)) {
		return value.includes(option);
	}

	if (typeof value === "string") {
		return value.includes(option);
	}

	return false;
};

type ServiceOptions =
	| "evaluators"
	| "uni_complete_report"
	| "palm_report_type"
	| "palm_report"
	| "data_save_time"
	| "ia"
	| "training_mode"
	| "support"
	| "cs_specialized"
	| "annotation"
	| "customize_report"
	| "worklab";

/**
 * Hook para acessar dados de assinatura
 * * @param {ServiceOptions} [keyService] - Opcional. Nome do serviço específico para retornar em `selectedService`.
 * @returns {Object} Dados consolidados da assinatura, plano atual e serviço selecionado.
 */
export const useDataSubscription = (keyService?: ServiceOptions) => {
	const subscription: Subscription = useSelector(
		(state: any) => state.configs.subscription_config
	);
	const plans: Plan[] = useSelector((state: any) => state.configs.data_plans);
	const freeReportEnabled = useSelector(
		(state: any) => state.configs.freeReportEnabled
	);
	const last_report_free = useSelector(
		(state: any) => state.configs.last_report_free
	);

	const getService = (serviceName: ServiceOptions) => {
		return subscription?.services?.find(
			(s: Service) => s.service === serviceName
		);
	};
	const currentPlan = plans?.find((p: Plan) => p.id === subscription?.plan_id);
	const hasLoadedSubscription = Array.isArray(subscription?.services);
	const hasServiceWorklab = isWorklabSubscription(subscription);
	const operationalDashboardAccess =
		hasOperationalDashboardAccess(subscription);

	const isPlanExpired = (expiredAt: string) => {
		if (!expiredAt) {
			return false;
		}

		const [datePart] = expiredAt.split(" ");

		const [year, month, day] = datePart.split("-").map(Number);

		const expirationDate = new Date(year, month - 1, day);

		const today = new Date();

		today.setHours(0, 0, 0, 0);

		return expirationDate < today;
	};

	const expiredDatePayment = isPlanExpired(subscription?.expires_at);

	return {
		currentPlan,
		hasLoadedSubscription,
		hasServiceWorklab,
		hasOperationalDashboardAccess: operationalDashboardAccess,
		enableServices: subscription?.services,
		lastSincronization: subscription?.last_sincronization,
		currentStatus: subscription?.status,
		expirationPlanDate: subscription?.expires_at,
		startPlanDate: subscription?.started_at,
		registredEvaluators: subscription?.registred_evaluators,
		registredUnifiedReport: subscription?.registred_uni_complete_report,
		lastReportFreeGenerated: last_report_free,
		freeReporEnabled: freeReportEnabled,
		registredPalmReport: subscription?.registred_palm_report,
		isPlanExpired: expiredDatePayment,
		userId: subscription?.user_id,
		isServiceBlocked: (service?: Service) =>
			isSubscriptionServiceBlocked(service),
		isLimitReached: (
			limitValue: Service["value"] | undefined | null,
			currentValue: number | undefined | null
		) => isSubscriptionLimitReached(limitValue, currentValue),
		...(keyService && { selectedService: getService(keyService) }),
	};
};
